import { FolderChecked, Ribbon } from 'icons';
import { useTranslation } from 'react-i18next';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { formatDate, formatDateAsUtc } from '@/utils/date';
import { getDueDateColor, getDueDateString } from '@/utils/dueDate';
import { getProgressBarText } from '@/utils/generateProgressBarText';
import { generateProgressText } from '@/utils/generateProgressText';
import { itemByLanguage } from '@/utils/language';

import { PathwayCard } from '@/components/pathway-card';

import PlanCardIcon from '../PlanCardComponents/PlanCardIcon';
import PlanCardTag from '../PlanCardComponents/PlanCardTag';
import PlanCardCategoryIcon from '../PlanCardContent/PlanCardCategoryIcon';

export interface TeamMemberPlanCardProps {
  link: string;
  category?: string;
  name?: Record<string, string>;
  isComplete: boolean;
  completedSteps?: number;
  totalSteps?: number;
  timeSpentOnPlan: string | null;
  dueDate?: string;
  completionDate?: string;
  /** Completion date of a previous assignment; shown while the current one is in progress. */
  lastCompletedDate?: string;
}

/** One person's progress on one plan (My Plans and Reports > Team Member). */
const TeamMemberPlanCard: React.FC<TeamMemberPlanCardProps> = ({
  link,
  category,
  name,
  isComplete,
  completedSteps,
  totalSteps,
  timeSpentOnPlan,
  dueDate,
  completionDate,
  lastCompletedDate,
}) => {
  const { t } = useTranslation();

  const percentComplete = totalSteps
    ? Math.round(((completedSteps ?? 0) / totalSteps) * 100)
    : undefined;
  const showProgress =
    !isComplete && percentComplete !== undefined && percentComplete !== 100;

  return (
    <PathwayCard.Root
      className="animate-[fadeIn_linear_0.3s]"
      data-testid="MyPlan"
      link={link}
    >
      <PathwayCard.Media>
        <PlanCardCategoryIcon category={category} />
      </PathwayCard.Media>
      <PathwayCard.Body>
        <PathwayCard.Label>
          {t(mapCategoryToAliasTranslation(category))}
        </PathwayCard.Label>
        <PathwayCard.Title>{itemByLanguage(name, '')}</PathwayCard.Title>
        {isComplete ? (
          <PathwayCard.Subtitle tone="success" data-testid="PlanTaskCount">
            {generateProgressText({
              completedStepCount: completedSteps,
              isComplete,
              timeSpentOnPlan,
              stepCount: totalSteps,
            })}
          </PathwayCard.Subtitle>
        ) : (
          <PathwayCard.Subtitle data-testid="PlanTaskCount">
            {getProgressBarText({
              completedStepCount: completedSteps,
              isComplete,
              percentComplete,
              stepCount: totalSteps,
              timeSpentOnPlan,
            })}
          </PathwayCard.Subtitle>
        )}
      </PathwayCard.Body>
      <PathwayCard.Meta className="gap-2">
        {!isComplete && !!lastCompletedDate && (
          <PlanCardIcon
            icon={<FolderChecked className="size-6" />}
            tooltip={
              <p className="text-variant-body2 text-grayscale-gray7 font-normal">
                {t('TrainingPlans.statusOptions.lastCompleted')}{' '}
                {formatDate(lastCompletedDate)}
              </p>
            }
          />
        )}
        {!isComplete && !!dueDate && (
          <PlanCardTag
            color={getDueDateColor(dueDate)}
            text={getDueDateString(dueDate)}
            tooltip={t('TrainingPlans.accountability.due.dueDate', {
              dueDate: formatDateAsUtc(dueDate),
            })}
          />
        )}
        {isComplete && (
          <PlanCardIcon
            icon={<Ribbon className="size-7!" />}
            tooltip={
              <>
                <strong>{t('TrainingPlans.statusOptions.completed')}:</strong>{' '}
                {completionDate ? formatDate(completionDate) : t('Generic.na')}
              </>
            }
          />
        )}
      </PathwayCard.Meta>
      {showProgress && (
        <PathwayCard.Progress
          aria-label="Plan progress"
          value={percentComplete}
        />
      )}
    </PathwayCard.Root>
  );
};

export default TeamMemberPlanCard;
